/**
 * Parses a raw text research report (Vietnamese or English) into structured data.
 * Handles both plain-text labels and bold-markdown labels (**Label:**).
 * Handles both bullet-list and markdown-table formats for metrics/funding/community.
 */
export function parseReport(text) {
  if (!text) return null;

  // Strip triple-backtick fences (Claude sometimes wraps template sections)
  let clean = text.replace(/^```[^\n]*\n?/gm, '').replace(/```\s*$/gm, '');
  // Strip bold markers globally so **Label:** becomes Label:
  clean = clean.replace(/\*\*/g, '');
  // Strip italic markers
  clean = clean.replace(/\*([^*]+)\*/g, '$1');
  // Strip blockquote markers that Claude sometimes adds (> Note: ...)
  clean = clean.replace(/^>\s*/gm, '');

  const lines = clean.split('\n');
  const sections = {};
  let current = 'header';
  let buf = [];

  for (const line of lines) {
    const secMatch = line.match(/^## (\d+)\.\s+(.+)/);
    if (secMatch) {
      sections[current] = buf.join('\n');
      current = `sec${secMatch[1]}`;
      buf = [line];
    } else {
      buf.push(line);
    }
  }
  sections[current] = buf.join('\n');

  return {
    meta:         parseMeta(sections.header || ''),
    tldr:         parseBlock(sections.sec1),
    overview:     parseOverview(sections.sec2 || ''),
    products:     parseProducts(sections.sec3 || ''),
    tokenomics:   parseTokenomics(sections.sec4 || ''),
    applications: parseApplications(sections.sec5 || ''),
    achievements: parseAchievements(sections.sec6 || ''),
    team:         parseTeam(sections.sec7 || ''),
    events:       parseEvents(sections.sec8 || ''),
    potential:    parsePotential(sections.sec9 || ''),
  };
}

// ── helpers ──────────────────────────────────────────────────────────────────

/** Split text into non-empty trimmed lines */
function ls(text) {
  return (text || '').split('\n').map(l => l.trim()).filter(Boolean);
}

/** Join non-header, non-bullet lines into a paragraph */
function parseBlock(text) {
  return ls(text).filter(l => !l.startsWith('##') && !l.startsWith('---')).join(' ').trim();
}

/**
 * Try to extract a value from line matching any of the given key patterns.
 * Keys can be strings (converted to case-insensitive regex) or RegExp.
 */
function kv(line, ...keys) {
  for (const key of keys) {
    const rx = typeof key === 'string'
      ? new RegExp(`^-?\\s*${key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s*[:\\s]\\s*(.+)`, 'i')
      : key;
    const m = line.match(rx);
    if (m) return m[1].trim();
  }
  return null;
}

/**
 * Parse a markdown table section into an array of objects.
 * Skips header and separator rows. Returns [{col0, col1, col2, ...}]
 */
function parseTable(text) {
  const rows = [];
  let headers = null;
  for (const l of ls(text)) {
    if (!l.startsWith('|')) continue;
    if (l.match(/^\|[\s\-|]+\|$/)) continue; // separator ---
    const cols = l.split('|').map(c => c.trim()).filter((_, i, a) => i > 0 && i < a.length - 1);
    if (!headers) { headers = cols; continue; }
    const obj = {};
    headers.forEach((h, i) => { obj[h] = cols[i] || ''; });
    rows.push(obj);
  }
  return rows;
}

function parseMeta(text) {
  const nameMatch = text.match(/(?:Project|Dự án)\s*:\s*([^|]+)\|\s*Token\s*:\s*(\$[\w]+)/i);
  const dateMatch = text.match(/(?:Date|Ngày)\s*:\s*([\d/-]+)/i);
  return {
    name:   nameMatch?.[1]?.trim() || '',
    ticker: nameMatch?.[2]?.trim() || '',
    date:   dateMatch?.[1] || '',
  };
}

function parseOverview(text) {
  const result = {};
  for (const l of ls(text)) {
    if (l.startsWith('|') || l.startsWith('-')) {
      result.category  = result.category  || kv(l, 'Category', 'Danh mục') || '';
      result.chain     = result.chain     || kv(l, 'Chain', 'Blockchain', 'Chuỗi') || '';
      result.problem   = result.problem   || kv(l, 'Problem', 'Vấn đề giải quyết', 'Vấn đề') || '';
      result.stage     = result.stage     || kv(l, 'Stage', 'Giai đoạn') || '';
      result.website   = result.website   || kv(l, 'Website') || '';
      result.whitepaper = result.whitepaper || kv(l, 'Whitepaper') || '';

      const tokenLine = kv(l, 'Token');
      if (tokenLine) {
        result.ticker = result.ticker || tokenLine.match(/(\$[\w]+)/)?.[1] || '';
        result.launch = result.launch || tokenLine.match(/(?:Launch|Ra mắt)\s*:\s*(.+?)(?:\s*\|.*)?$/i)?.[1]?.trim() || '';
      }

      const priceLine = kv(l, 'Price', 'Giá');
      if (priceLine) {
        result.price = result.price || priceLine.split('|')[0]?.trim() || '';
        result.mcap  = result.mcap  || priceLine.match(/(?:Market Cap|Vốn hóa)\s*:\s*([^|]+)/i)?.[1]?.trim() || '';
        result.fdv   = result.fdv   || priceLine.match(/(?:FDV|Định giá hoàn toàn pha loãng)\s*:\s*([^|]+)/i)?.[1]?.trim() || '';
        result.vol   = result.vol   || priceLine.match(/(?:24h Vol|KL 24h|Khối lượng 24)\s*:\s*([^|]+)/i)?.[1]?.trim() || '';
      }

      // Also accept inline: - Giá: $3.15 | Vốn hóa: $3.78B | FDV: $6.3B | KL 24h: $280M
      if (!result.price) {
        const priceFull = l.match(/(?:Giá|Price)\s*:\s*([~$\d.,]+)/i)?.[1];
        if (priceFull) result.price = priceFull;
      }
    }
  }
  return result;
}

function parseProducts(text) {
  const result = { core: '', techStack: '', differentiator: '', completed: [], inProgress: [], upcoming: [] };
  let mode = null;

  for (const l of ls(text).filter(l => !l.startsWith('##'))) {
    // Mode triggers — match with or without colon, handles lines like "Sản phẩm cốt lõi:"
    if (/^(?:Core product|Sản phẩm cốt lõi)\s*:?$/i.test(l))       { mode = 'core'; continue; }
    if (/^(?:Tech stack|Công nghệ)\s*:?$/i.test(l))                  { mode = 'tech'; continue; }
    if (/^(?:Key differentiator|Điểm khác biệt)\s*:?$/i.test(l))    { mode = 'diff'; continue; }
    if (/^(?:Roadmap status|Trạng thái lộ trình)\s*:?$/i.test(l))   { mode = 'road'; continue; }

    // Inline value on same line: "Sản phẩm cốt lõi: description..."
    const coreInline = l.match(/^(?:Core product|Sản phẩm cốt lõi)\s*:\s*(.+)/i);
    if (coreInline) { result.core += ' ' + coreInline[1]; mode = 'core'; continue; }

    const techInline = l.match(/^(?:Tech stack|Công nghệ)\s*:\s*(.+)/i);
    if (techInline) { result.techStack += ' ' + techInline[1]; mode = 'tech'; continue; }

    const diffInline = l.match(/^(?:Key differentiator|Điểm khác biệt)\s*:\s*(.+)/i);
    if (diffInline) { result.differentiator += ' ' + diffInline[1]; mode = 'diff'; continue; }

    if (mode === 'road') {
      if (l.startsWith('✅') || l.match(/^-\s*(?:Completed|Đã hoàn thành)/i))
        result.completed.push(l.replace(/^✅\s*(?:Completed|Đã hoàn thành)\s*:\s*/i, '').replace(/^✅\s*/, '').trim());
      else if (l.startsWith('🔄') || l.match(/^-\s*(?:In progress|Đang thực hiện)/i))
        result.inProgress.push(l.replace(/^🔄\s*(?:In progress|Đang thực hiện)\s*:\s*/i, '').replace(/^🔄\s*/, '').trim());
      else if (l.startsWith('📅') || l.match(/^-\s*(?:Upcoming|Sắp tới)/i))
        result.upcoming.push(l.replace(/^📅\s*(?:Upcoming|Sắp tới)\s*:\s*/i, '').replace(/^📅\s*/, '').trim());
    } else if (mode === 'core') {
      result.core += ' ' + l;
    } else if (mode === 'tech') {
      result.techStack += ' ' + l;
    } else if (mode === 'diff') {
      result.differentiator += ' ' + l;
    }
  }

  return {
    core: result.core.trim(),
    techStack: result.techStack.trim(),
    differentiator: result.differentiator.trim(),
    completed: result.completed,
    inProgress: result.inProgress,
    upcoming: result.upcoming,
  };
}

function parseTokenomics(text) {
  const result = {
    totalSupply: '', circulating: '', fdvAtLaunch: '', mcapAtTge: '',
    distribution: [], utility: [], inflation: '', unlockRisk: '', score: '', scoreReason: '',
    update: { outdated: '', unlockProgress: '', lockChanges: '', newBurn: '', addedUtilities: '', thesis: '' },
  };

  // Sub-line labels for the "Cập nhật & độ lỗi thời của tokenomics" block
  const UPDATE_FIELDS = [
    ['outdated',       /Mức độ lỗi thời/i],
    ['unlockProgress', /Tiến độ mở khóa/i],
    ['lockChanges',    /Thay đổi token lock|Thay đổi.*unlock/i],
    ['newBurn',        /Token burn mới|burn mới/i],
    ['addedUtilities', /Tiện ích bổ sung|additional utilit/i],
    ['thesis',         /Hàm ý đầu tư/i],
  ];

  for (const l of ls(text).filter(l => !l.startsWith('##'))) {
    for (const [key, re] of UPDATE_FIELDS) {
      if (!result.update[key] && re.test(l)) {
        const v = l.replace(/^[\s\-•]*/, '').split(/:\s*/).slice(1).join(': ').trim();
        if (v) result.update[key] = v;
      }
    }
    result.totalSupply = result.totalSupply || kv(l, 'Total supply', 'Tổng cung') || '';
    // "Lưu thông (hiện tại):" or "Lưu thông (TGE):" — flexible parens
    if (!result.circulating) {
      const cm = l.match(/^(?:Circulating|Lưu th[oô]ng)\s*[^:]*:\s*(.+)/i);
      if (cm) result.circulating = cm[1].trim();
    }
    result.fdvAtLaunch = result.fdvAtLaunch || kv(l, 'FDV at launch', 'FDV khi ra mắt', 'FDV') || '';
    if (!result.mcapAtTge) {
      const mm = l.match(/^(?:Market cap|Vốn hóa)\s*[^:]*:\s*(.+)/i);
      if (mm) result.mcapAtTge = mm[1].trim();
    }
    result.unlockRisk = result.unlockRisk || l.match(/^⚠️\s*(?:Unlock risk|Rủi ro mở khóa)\s*:\s*(.+)/i)?.[1]?.trim() || '';
    result.score      = result.score      || l.match(/^(?:Tokenomics score|Điểm tokenomics)\s*:\s*(.+)/i)?.[1]?.trim() || '';
    result.scoreReason = result.scoreReason || kv(l, 'Reason', 'Lý do') || '';

    // Distribution table: │ Label │ X% │ Vesting │
    const row = l.match(/[│|]\s*([^│|]+?)\s*[│|]\s*([\d.]+%)\s*[│|]\s*([^│|]+?)\s*[│|]/);
    if (row && !/(?:Allocation|Phân bổ|%|──|══)/i.test(row[1]) && row[1].trim().length > 1) {
      result.distribution.push({ label: row[1].trim(), percent: row[2].trim(), vesting: row[3].trim() });
    }

    // Utility: [tag1] [tag2] or "- Staking: desc"
    if (/^(?:Token utility|Tiện ích token)\s*:/i.test(l)) {
      const rest = l.replace(/^(?:Token utility|Tiện ích token)\s*:\s*/i, '');
      result.utility = rest.match(/\[([^\]]+)\]/g)?.map(s => s.replace(/[[\]]/g, '')) || [rest];
    }
    // Utility as sub-bullets
    if (mode_isUtility(result) && l.startsWith('-')) {
      const utItem = l.replace(/^-\s*/, '').split(':')[0].trim();
      if (utItem && !result.utility.includes(utItem)) result.utility.push(utItem);
    }

    if (/^(?:Inflation|Lạm phát)/i.test(l)) {
      result.inflation = l.replace(/^(?:Inflation[^:]*|Lạm phát[^:]*)\s*:\s*/i, '').trim();
    }
  }

  return result;
}

function mode_isUtility(result) {
  // Utility is a token utility sub-bullet detector — not needed as mode but avoids crash
  return false;
}

function parseApplications(text) {
  const result = { useCases: [], partnerships: [], metrics: {} };
  let mode = null;

  for (const l of ls(text).filter(l => !l.startsWith('##'))) {
    // Mode triggers (with or without inline content)
    if (/^(?:Live use cases|Trường hợp sử dụng thực tế)\s*:?/i.test(l)) { mode = 'uses'; continue; }
    if (/^(?:Active partnerships|Đối tác đang hoạt động)\s*:?/i.test(l)) { mode = 'part'; continue; }
    if (/^(?:On-chain metrics|Chỉ số on-chain)/i.test(l))               { mode = 'metr'; continue; }

    if (mode === 'uses' && l.startsWith('-')) {
      result.useCases.push(l.slice(1).trim());
    } else if (mode === 'part' && l.startsWith('-')) {
      const m = l.slice(1).trim().match(/^([^:]+):\s*(.+)/);
      if (m) result.partnerships.push({ name: m[1].trim(), desc: m[2].trim() });
      else result.partnerships.push({ name: l.slice(1).trim(), desc: '' });
    } else if (mode === 'metr') {
      // Bullet format: "- TVL: $X"
      if (l.startsWith('-')) {
        const content = l.slice(1).trim();
        extractMetric(result.metrics, content);
      }
      // Markdown table format: "| Chỉ số | Giá trị |" rows
      if (l.startsWith('|') && !l.match(/^\|[\s\-|]+\|$/)) {
        const cols = l.split('|').map(c => c.trim()).filter((_, i, a) => i > 0 && i < a.length - 1);
        if (cols.length >= 2 && !/^(?:Chỉ số|Metric|Label)/i.test(cols[0])) {
          extractMetric(result.metrics, `${cols[0]}: ${cols[1]}`);
        }
      }
    }
  }
  return result;
}

function extractMetric(metrics, content) {
  const tvl  = content.match(/^(?:TVL|Tổng TVL)\s*[^:]*:\s*(.+)/i);
  const dau  = content.match(/^(?:Daily active|Người dùng hoạt động|Giao dịch trung bình)\s*[^:]*:\s*(.+)/i);
  const txn  = content.match(/^(?:Total transactions|Tổng giao dịch\s*(?:tích lũy)?)\s*:\s*(.+)/i);
  const wal  = content.match(/^(?:Unique wallets|Ví duy nhất)\s*:\s*(.+)/i);
  const dapp = content.match(/^(?:Active dApps|Ứng dụng phi tập trung)\s*[^:]*:\s*(.+)/i);
  if (tvl)  metrics['tvl']                = tvl[1].trim();
  if (dau)  metrics['daily_active']        = dau[1].trim();
  if (txn)  metrics['total_transactions']  = txn[1].trim();
  if (wal)  metrics['unique_wallets']      = wal[1].trim();
  if (dapp) metrics['active_dapps']        = dapp[1].trim();
}

function parseAchievements(text) {
  const result = { funding: {}, community: {}, testnet: {}, kpis: [] };
  let mode = null;

  for (const l of ls(text).filter(l => !l.startsWith('##'))) {
    // Mode triggers
    if (/^(?:Funding|Gọi vốn)\s*:?/i.test(l) && !l.startsWith('|'))       { mode = 'fund'; continue; }
    if (/^(?:Community|Cộng đồng)\s*[&\w\s]*:?/i.test(l) && !l.startsWith('|')) { mode = 'comm'; continue; }
    if (/^(?:Testnet|Beta)/i.test(l) && !l.startsWith('|'))                 { mode = 'test'; continue; }
    if (/^(?:Other KPIs|Chỉ số KPI khác)/i.test(l))                         { mode = 'kpi';  continue; }

    const val = l.replace(/^-\s*/, '');

    if (mode === 'fund') {
      // Bullet format
      result.funding.total      = result.funding.total      || val.match(/^(?:Total raised|Tổng gọi vốn)\s*:\s*(.+)/i)?.[1]?.trim() || '';
      result.funding.rounds     = result.funding.rounds     || val.match(/^(?:Rounds|Các vòng)\s*:\s*(.+)/i)?.[1]?.trim() || '';
      result.funding.investors  = result.funding.investors  || val.match(/^(?:Lead investors?|Nhà đầu tư dẫn đầu)\s*:\s*(.+)/i)?.[1]?.trim() || '';
      result.funding.valuation  = result.funding.valuation  || val.match(/^(?:Valuation|Định giá vòng gần nhất)\s*[^:]*:\s*(.+)/i)?.[1]?.trim() || '';

      // Table format: | Vòng | Số tiền | Thời gian | Nhà đầu tư |
      if (l.startsWith('|') && !l.match(/^\|[\s\-|]+\|$/)) {
        const cols = l.split('|').map(c => c.trim()).filter((_, i, a) => i > 0 && i < a.length - 1);
        if (cols.length >= 2 && /^(?:Tổng|Total)/i.test(cols[0])) {
          result.funding.total = result.funding.total || cols[1];
        }
        // Aggregate round info
        if (cols.length >= 2 && cols[1]?.match(/^\$/) && !/^(?:Vòng|Round|Tổng|Total)/i.test(cols[0])) {
          const rnd = `${cols[0]}: ${cols[1]}`;
          if (!result.funding.rounds) result.funding.rounds = rnd;
          else if (!result.funding.rounds.includes(cols[0])) result.funding.rounds += ` | ${rnd}`;
        }
      }
    } else if (mode === 'comm') {
      // Bullet format
      result.community.twitter  = result.community.twitter  || val.match(/^(?:X\/Twitter|Twitter)\s*(?:\([^)]*\))?\s*:\s*(.+)/i)?.[1]?.trim() || '';
      result.community.discord  = result.community.discord  || val.match(/^Discord\s*:\s*(.+)/i)?.[1]?.trim() || '';
      result.community.telegram = result.community.telegram || val.match(/^Telegram\s*:\s*(.+)/i)?.[1]?.trim() || '';
      result.community.github   = result.community.github   || val.match(/^GitHub\s*:\s*(.+)/i)?.[1]?.trim() || '';

      // Table format: | Kênh | Quy mô |
      if (l.startsWith('|') && !l.match(/^\|[\s\-|]+\|$/)) {
        const cols = l.split('|').map(c => c.trim()).filter((_, i, a) => i > 0 && i < a.length - 1);
        if (cols.length >= 2 && !/^(?:Kênh|Channel|Quy mô|Size)/i.test(cols[0])) {
          const ch = cols[0].toLowerCase();
          const sz = cols[1];
          if (/twitter|x\//i.test(ch))     result.community.twitter  = result.community.twitter  || sz;
          if (/discord/i.test(ch))         result.community.discord  = result.community.discord  || sz;
          if (/telegram/i.test(ch))        result.community.telegram = result.community.telegram || sz;
          if (/github/i.test(ch))          result.community.github   = result.community.github   || sz;
        }
      }
    } else if (mode === 'test') {
      result.testnet.users = result.testnet.users || val.match(/^(?:Testnet users?|Người dùng testnet)\s*:\s*(.+)/i)?.[1]?.trim() || '';
      result.testnet.txns  = result.testnet.txns  || val.match(/^(?:Testnet transactions?|Giao dịch testnet)\s*:\s*(.+)/i)?.[1]?.trim() || '';
      result.testnet.beta  = result.testnet.beta  || val.match(/^(?:Beta programs?|Chương trình beta)\s*:\s*(.+)/i)?.[1]?.trim() || '';
    } else if (mode === 'kpi' && l.startsWith('-')) {
      result.kpis.push(l.slice(1).trim());
    }
  }

  // Fallback: scan for total raised anywhere in funding section
  if (!result.funding.total) {
    const totalMatch = text.match(/(?:Tổng gọi vốn|Total raised)[^:]*:\s*([^\n]+)/i);
    if (totalMatch) result.funding.total = totalMatch[1].trim();
  }
  if (!result.funding.investors) {
    const invMatch = text.match(/(?:Nhà đầu tư dẫn đầu|Lead investors?)[^:]*:\s*([^\n]+)/i);
    if (invMatch) result.funding.investors = invMatch[1].trim();
  }

  return result;
}

function parseTeam(text) {
  const members = [];
  for (const l of ls(text)) {
    if (l.startsWith('|') && !l.match(/^\|[\s\-|]+\|$/) && !/^[\|\s]*(?:Name|Tên|Member)/i.test(l)) {
      const cols = l.split('|').map(c => c.trim()).filter((_, i, a) => i > 0 && i < a.length - 1);
      if (cols.length >= 3 && cols[0].length > 1) {
        members.push({ name: cols[0], role: cols[1], background: cols[2], credibility: cols[3] || '' });
      }
    }
  }

  let assessment = '', reason = '', flags = '';
  for (const l of ls(text).filter(l => !l.startsWith('#') && !l.startsWith('|') && !l.match(/^[\s\-|]+$/))) {
    assessment = assessment || l.match(/^(?:Team assessment|Đánh giá đội ngũ)\s*:\s*(.+)/i)?.[1]?.trim() || '';
    reason     = reason     || (!assessment ? '' : l.match(/^(?:Reason|Lý do)\s*:\s*(.+)/i)?.[1]?.trim() || '');
    flags      = flags      || l.match(/^(?:Red flags?|Dấu hiệu rủi ro)\s*:\s*(.+)/i)?.[1]?.trim() || '';
  }
  return { members, assessment, reason, flags };
}

function parseEvents(text) {
  // Split into sub-sections 8A, 8B, 8C (also handles ### headers with Vietnamese names)
  const subSecs = {};
  let cur = null;
  let buf = [];

  for (const l of text.split('\n')) {
    const m = l.match(/^###\s+8([ABC])\./i);
    if (m) {
      if (cur) subSecs[cur] = buf.join('\n');
      cur = m[1].toUpperCase();
      buf = [];
    } else {
      buf.push(l);
    }
  }
  if (cur) subSecs[cur] = buf.join('\n');

  // Strip bold markers from sub-sections
  const stripBold = s => (s || '').replace(/\*\*/g, '').replace(/^>\s*/gm, '');

  return {
    catalysts: parseCatalysts(stripBold(subSecs['A'])),
    unlocks:   parseUnlocks(stripBold(subSecs['B'])),
    roadmap:   parseRoadmap(stripBold(subSecs['C'])),
  };
}

function parseCatalysts(text) {
  const events = [];
  const seen = new Set();

  for (const l of ls(text)) {
    if (!l.includes('│') && !l.includes('|')) continue;
    if (l.match(/^[│|][\s─┼├╠╔╚═\-|]+[│|]$/)) continue;
    if (/^[│|]\s*(?:Date|Ngày|Sự kiện|Event|Tác động|Impact)/i.test(l)) continue;

    const sep = l.includes('│') ? '│' : '|';
    const cols = l.split(sep).map(c => c.trim()).filter((_, i, a) => i > 0 && i < a.length - 1);
    if (cols.length < 2) continue;

    const dateRaw = cols[0];
    const eventText = cols[1];
    const impact = cols[2] || '';

    // Skip if date looks like a continuation or empty
    if (!dateRaw || dateRaw.match(/^[-─═\s]*$/) || seen.has(eventText)) continue;
    seen.add(eventText);

    // Only take rows where date looks like a real date or known placeholder
    if (dateRaw.match(/\d{4}/) || /TBA|Chưa xác định|Đồn đoán|ước tính/i.test(dateRaw) || dateRaw.match(/^\d{4}/)) {
      events.push({ date: dateRaw, event: eventText, impact: impact });
    }
  }

  let sellRating = '', sellReason = '';
  for (const l of ls(text)) {
    sellRating = sellRating || l.match(/^(?:Sell pressure rating|Mức độ áp lực bán)\s*:\s*(.+)/i)?.[1]?.trim() || '';
    sellReason = sellReason || l.match(/^(?:Reason|Lý do)\s*:\s*(.+)/i)?.[1]?.trim() || '';
  }
  return { events, sellRating, sellReason };
}

function parseUnlocks(text) {
  const schedule = [];

  for (const l of ls(text)) {
    if (!l.includes('│') && !l.includes('|')) continue;
    if (l.match(/^[│|][\s─┼├╠╔╚═\-|]+[│|]$/)) continue;

    const sep = l.includes('│') ? '│' : '|';
    const cols = l.split(sep).map(c => c.trim()).filter((_, i, a) => i > 0 && i < a.length - 1);
    if (cols.length < 4) continue;
    if (/^(?:Tháng|Month|Danh mục|Category)/i.test(cols[0])) continue;
    if (cols[0].match(/^[-─═\s]*$/)) continue;

    schedule.push({ month: cols[0], category: cols[1], tokens: cols[2], pct: cols[3], value: cols[4] || '' });
  }

  const cumulative = {};
  for (const l of ls(text)) {
    const c3  = l.match(/(?:Tổng mở khóa 3 tháng|Cumulative.*?3 month)\s*[^:]*:\s*(.+)/i);
    const c6  = l.match(/(?:Tổng mở khóa 6 tháng|Cumulative.*?6 month)\s*[^:]*:\s*(.+)/i);
    const c12 = l.match(/(?:Tổng mở khóa 12 tháng|Cumulative.*?12 month)\s*[^:]*:\s*(.+)/i);
    if (c3)  cumulative['3m']  = c3[1].trim();
    if (c6)  cumulative['6m']  = c6[1].trim();
    if (c12) cumulative['12m'] = c12[1].trim();
  }

  let sellRating = '', sellReason = '';
  for (const l of ls(text)) {
    sellRating = sellRating || l.match(/^(?:Sell pressure rating|Mức độ áp lực bán)\s*:\s*(.+)/i)?.[1]?.trim() || '';
    sellReason = sellReason || l.match(/^(?:Reason|Lý do)\s*:\s*(.+)/i)?.[1]?.trim() || '';
  }
  return { schedule, cumulative, sellRating, sellReason };
}

function parseRoadmap(text) {
  const result = { onSchedule: '', delivered: [], delayed: [], abandoned: [], next: [], velocity: {} };

  for (const l of ls(text)) {
    result.onSchedule = result.onSchedule || l.match(/^(?:On-schedule rating|Mức độ đúng hạn)\s*:\s*(.+)/i)?.[1]?.trim() || '';

    if (l.startsWith('✅') || l.match(/^-\s*✅/)) result.delivered.push(l.replace(/^[-\s]*✅\s*/, ''));
    if (l.startsWith('⚠️') || l.match(/^-\s*⚠️/)) result.delayed.push(l.replace(/^[-\s]*⚠️\s*/, ''));
    if (l.startsWith('❌') || l.match(/^-\s*❌/)) result.abandoned.push(l.replace(/^[-\s]*❌\s*/, ''));

    // "Quý 1 2025: ..." or "Q1 2025: ..."
    const qm = l.match(/^(?:Quý\s*(\d+)|Q(\d+))\s+(\d{4})\s*:\s*(.+)/i);
    if (qm) result.next.push({ quarter: `Quý ${qm[1] || qm[2]} ${qm[3]}`, items: qm[4].trim() });

    result.velocity.commits      = result.velocity.commits      || l.match(/^-?\s*(?:GitHub commits?|Commit GitHub)\s*[^:]*:\s*(.+)/i)?.[1]?.trim();
    result.velocity.lastRelease  = result.velocity.lastRelease  || l.match(/^-?\s*(?:Last release|Phiên bản gần nhất)\s*:\s*(.+)/i)?.[1]?.trim();
    result.velocity.contributors = result.velocity.contributors || l.match(/^-?\s*(?:Active contributors?|Cộng tác viên tích cực)\s*:\s*(.+)/i)?.[1]?.trim();
    result.velocity.trend        = result.velocity.trend        || l.match(/^-?\s*(?:Velocity trend|Xu hướng tốc độ)\s*:\s*(.+)/i)?.[1]?.trim();
  }

  return result;
}

function parsePotential(text) {
  const result = { bull: '', bear: '', risks: [], verdictStars: 0, verdictText: '' };
  let mode = null;
  let afterVerdict = false;

  for (const l of ls(text).filter(l => !l.startsWith('#'))) {
    // Mode triggers (standalone label lines OR inline)
    if (/^(?:Bull case|Kịch bản tăng giá)\s*:?\s*$/i.test(l))  { mode = 'bull'; continue; }
    if (/^(?:Bear case|Kịch bản giảm giá)\s*:?\s*$/i.test(l))  { mode = 'bear'; continue; }
    if (/^(?:Risk matrix|Ma trận rủi ro)\s*:?\s*$/i.test(l))    { mode = 'risk'; continue; }

    // Inline: "Kịch bản tăng giá: content..."
    const bullInline = l.match(/^(?:Bull case|Kịch bản tăng giá)\s*:\s*(.+)/i);
    if (bullInline) { result.bull += ' ' + bullInline[1]; mode = 'bull'; continue; }

    const bearInline = l.match(/^(?:Bear case|Kịch bản giảm giá)\s*:\s*(.+)/i);
    if (bearInline) { result.bear += ' ' + bearInline[1]; mode = 'bear'; continue; }

    // "Overall verdict / Kết luận tổng thể: ⭐X/5"
    const vm = l.match(/^(?:Overall verdict|Kết luận tổng thể)\s*:\s*⭐?([\d.]+)\/5/i);
    if (vm) { result.verdictStars = parseFloat(vm[1]); afterVerdict = true; mode = null; continue; }

    if (afterVerdict && l && !result.verdictText && !l.startsWith('-')) {
      result.verdictText = l.trim();
      continue;
    }

    if (mode === 'bull') {
      // Stop when hitting a new section
      if (/^(?:Bear case|Kịch bản giảm giá|Risk matrix|Ma trận)/i.test(l)) { mode = 'bear'; continue; }
      result.bull += ' ' + l;
    } else if (mode === 'bear') {
      if (/^(?:Risk matrix|Ma trận rủi ro)/i.test(l)) { mode = 'risk'; continue; }
      result.bear += ' ' + l;
    } else if (mode === 'risk' && l.startsWith('-')) {
      // "- Rủi ro pháp lý: 🟢 Thấp — reason" or "- Legal risk: 🟢 Low — reason"
      const m = l.match(/^-\s*([^:]+):\s*([🟢🟡🔴]\S*)\s+(.*)/u);
      if (m) result.risks.push({ label: m[1].trim(), rating: m[2].trim(), reason: m[3].trim() });
    }
  }

  result.bull = result.bull.trim();
  result.bear = result.bear.trim();
  return result;
}
